import { Box, Link, SvgIcon, Tooltip, Typography } from '@mui/material';
import { DragVertical as DragIndicatorIcon } from '@opengov/react-capital-assets';
import React from 'react';

import { CloseButton } from '../Components/CloseButton';
import { NAV_COLOR_PALETTE, tooltipOffsetProps } from '../Components/styleConstants';
import { DATA_TEST, getDynamicDataTestId } from '../enums';
import { useIsContentOverflowing } from '../utils/hooks/useIsContentOverflowing';

interface IFavoritesSectionItemProps {
  label: string;
  textRef: React.MutableRefObject<any>;
  deleteButtonRef: React.MutableRefObject<HTMLButtonElement>;
  linkComponent?: React.ElementType;
  url?: string;
  closeFavoritesSubmenu(): void;
  onClick?(): void;
  handleDelete(): Promise<void>;
}
export const FavoritesSectionItem: React.FC<IFavoritesSectionItemProps> = ({
  label,
  url,
  linkComponent,
  textRef,
  deleteButtonRef,
  closeFavoritesSubmenu,
  onClick,
  handleDelete
}) => {
  const textStyles = {
    pl: 1,
    py: 0,
    textDecoration: 'none',
    border: 'none',
    width: '100%', // For focus outline
    textAlign: 'inherit',
    underline: 'none',
    color: NAV_COLOR_PALETTE.primaryGray,
    ':visited': {
      color: NAV_COLOR_PALETTE.primaryGray
    },
    backgroundColor: 'inherit',
    ':focus-visible, &.Mui-focusVisible': {
      outline: `2px solid ${NAV_COLOR_PALETTE.focus}`,
      borderRadius: 1,
      outlineOffset: -2
    }
  };
  const isOverflowing = useIsContentOverflowing(textRef);

  const handleKeyDown = (event: React.KeyboardEvent, refToFocus: React.RefObject<HTMLElement>) => {
    if ((event.key === 'ArrowRight' || event.key === 'ArrowLeft') && refToFocus?.current) {
      refToFocus.current.focus();
    }
  };
  const [awaitingOnClickResponse, setAwaitingOnClickResponse] = React.useState(false);
  return (
    <Tooltip
      title={isOverflowing ? label : ''}
      placement="top"
      enterNextDelay={500}
      disableInteractive={true}
      {...tooltipOffsetProps}
    >
      <Box
        className="draggable-item"
        sx={{
          display: 'flex',
          width: '100%',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 1,
          border: `1px solid ${NAV_COLOR_PALETTE.tertiaryGray}`,
          ':hover': {
            border: `1px solid ${NAV_COLOR_PALETTE.secondaryGray}`,
            '.dragIcon': {
              color: NAV_COLOR_PALETTE.secondaryGray
            }
          },
          ':active': {
            border: `1px solid ${NAV_COLOR_PALETTE.secondaryGray}`,
            '.dragIcon': {
              color: NAV_COLOR_PALETTE.secondaryGray
            }
          },
          borderRadius: 1
        }}
      >
        <SvgIcon
          inheritViewBox
          component={DragIndicatorIcon}
          className="dragIcon"
          sx={{ color: NAV_COLOR_PALETTE.tertiaryGray }}
        />
        <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden', height: '80%', alignItems: 'center' }}>
          {url ? (
            <Link
              href={url}
              data-test={getDynamicDataTestId(DATA_TEST.favorites_item, `${label}_link`)}
              underline="none"
              color={NAV_COLOR_PALETTE.primaryGray}
              component={linkComponent || 'a'}
              tabIndex={-1}
              sx={{
                ...textStyles,
                ':visited': {
                  color: NAV_COLOR_PALETTE.primaryGray
                },
                display: 'block'
              }}
              variant="body2"
              noWrap={true}
              ref={textRef}
              onKeyDown={(e) => handleKeyDown(e, deleteButtonRef)}
              onClick={closeFavoritesSubmenu}
            >
              {label}
            </Link>
          ) : (
            <Typography
              data-test={getDynamicDataTestId(DATA_TEST.favorites_item, label)}
              variant="body2"
              component={'button'}
              tabIndex={-1}
              sx={textStyles}
              noWrap={true}
              ref={textRef}
              onKeyDown={(e) => handleKeyDown(e, deleteButtonRef)}
              onClick={onClick}
            >
              {label}
            </Typography>
          )}
        </Box>

        <CloseButton
          onKeyDown={(e) => handleKeyDown(e, textRef)}
          ref={deleteButtonRef}
          onClick={async () => {
            // Prevent spamming the API by waiting for the onClick function to complete before calling it again
            if (awaitingOnClickResponse) return;
            setAwaitingOnClickResponse(true);
            await handleDelete();
            setAwaitingOnClickResponse(false);
          }}
          loading={awaitingOnClickResponse}
          tabIndex={-1}
          toolTipText="Remove from your Favorites"
          dataTest={getDynamicDataTestId(DATA_TEST.delete_item, label)}
          styleOverrides={{ borderRadius: '50%' }}
        />
      </Box>
    </Tooltip>
  );
};
